'use client';

import { cn } from '../utils/cn';

export type CreditsHistoryFilter = 'all' | 'topup' | 'deduction';

export interface CreditsHistoryTransaction {
  id: string;
  title: string;
  type: 'topup' | 'deduction';
  status: string;
  statusColor: 'green' | 'red' | 'yellow';
  amount: number;
  createdAt: string;
}

const filters: { id: CreditsHistoryFilter; label: string }[] = [
  { id: 'all', label: 'Все' },
  { id: 'topup', label: 'Пополнение' },
  { id: 'deduction', label: 'Списание' },
];

interface CreditsHistoryScreenProps {
  filter: CreditsHistoryFilter;
  transactions?: CreditsHistoryTransaction[];
  isLoading?: boolean;
  onFilterChange: (filter: CreditsHistoryFilter) => void;
  /** Optional back-button handler. Web mobile passes this so the user
   * can leave without browser chrome; the miniapp leaves it undefined
   * and relies on Telegram's native BackButton. */
  onBack?: () => void;
}

export function CreditsHistoryScreen({
  filter,
  transactions,
  isLoading = false,
  onFilterChange,
  onBack,
}: CreditsHistoryScreenProps) {
  return (
    <div className="flex min-h-screen flex-col">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-background px-4 pb-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Назад"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#242424]/40"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="#fafafa"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4L5 10L11 16" />
              <line x1="5" y1="10" x2="16" y2="10" />
            </svg>
          </button>
        )}
        <h1 className="flex-1 font-benzin text-base text-[#f2f2f7]">
          История транзакции
        </h1>
      </div>

      <div className="mb-4 flex gap-2 px-4">
        {filters.map((item) => (
          <button
            key={item.id}
            onClick={() => onFilterChange(item.id)}
            className={cn(
              'rounded-full px-4 py-2 text-xs font-semibold transition-colors',
              filter === item.id
                ? 'bg-white text-black'
                : 'bg-white/10 text-white',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="flex-1 px-4">
        {isLoading ? (
          <div className="space-y-3 rounded-xl bg-white/5 p-2.5">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="flex items-center gap-3 py-2.5">
                <div className="h-10 w-10 animate-pulse rounded-full bg-white/10" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
                  <div className="h-3 w-16 animate-pulse rounded bg-white/10" />
                </div>
                <div className="space-y-1.5 text-right">
                  <div className="h-4 w-12 animate-pulse rounded bg-white/10" />
                  <div className="h-3 w-20 animate-pulse rounded bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        ) : transactions && transactions.length > 0 ? (
          <div className="rounded-xl bg-white/5 px-2.5">
            {transactions.map((tx, index) => (
              <div
                key={tx.id}
                className={cn(
                  'flex items-center gap-3 py-2.5',
                  index < transactions.length - 1 && 'border-b border-[#3a3a3a]/40',
                )}
              >
                <div className="flex h-10 w-10 items-center justify-center">
                  {tx.type === 'topup' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9dd94a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 19V5" />
                      <path d="M5 12l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef6444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14" />
                      <path d="M19 12l-7 7-7-7" />
                    </svg>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-base tracking-tight text-[#fafafa]">{tx.title}</p>
                  <p className={cn(
                    'text-xs font-semibold',
                    tx.statusColor === 'green' && 'text-primary',
                    tx.statusColor === 'red' && 'text-[#ef6444]',
                    tx.statusColor === 'yellow' && 'text-brand-orange',
                  )}>
                    {tx.status}
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <span
                      className={cn(
                        'text-base tracking-tight',
                        tx.amount > 0 ? 'text-white' : 'text-[#ef6444]',
                      )}
                    >
                      {tx.amount}
                    </span>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src="/assets/gem-icon.svg" alt="" className="h-4 w-4" />
                  </div>
                  <p className="text-xs font-semibold text-[#aeaeb2]">
                    {formatDate(tx.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">Нет транзакций</p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month}, ${hours}:${minutes}`;
}
